import React, { useState, useEffect } from "react";
import moment from "moment";
import { Box, Button as MuiButton, Typography, useMediaQuery } from "@mui/material";
import {
  JobberTable,
  JobberSearch,
  JobberPagination,
  JobberListCard,
} from "../../../components/MuiTable/index.jsx";
import "./style.css";
import { handleAuth } from "../../../components/Login/Auth.jsx";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AxiosInstance from "../../AxiosInstance.jsx";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import CustomSwal from "../../../components/Swal/CustomSwal.jsx";
import {
  DeleteIcone,
  EditIcon,
  LoaderComponent,
} from "../../../components/Icon/Index.jsx";
import { Grid } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import showToast from "../../../components/Toast/Toster.jsx";
import CustomTooltip from "../../../components/Tooltip/CustomTooltip.jsx";
import { useUserType } from "../../../contexts/UserTypeContext";
import { useSuperadminDateFormatter } from "../../../components/SuperadminDateFormatter/SuperadminDateFormatter";

const TruncatedText = ({ text }) => {
  const words = text.split(" ");
  if (words.length > 8) {
    const truncatedText = words.slice(0, 8).join(" ") + "...";
    return (
      <CustomTooltip title={text}>
        <Typography component="span">{truncatedText}</Typography>
      </CustomTooltip>
    );
  }
  return <Typography component="span">{text}</Typography>;
};

/** Row action tiles — same as quotes list (`src/Views/Admin/Quotes/Quote.jsx`) */
const PERMISSION_EDIT_ACTION_BG = "#DCFCE7";
const PERMISSION_EDIT_ICON_COLOR = "#047857";
const PERMISSION_DELETE_ACTION_BG = "#FEE2E2";
const PERMISSION_DELETE_ICON_COLOR = "#B91C1C";

/** Mobile collapse actions — same pattern as Materials & Labor / Plan */
const permissionMobileActionBtnBase = {
  width: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "8px",
  minHeight: 32,
  px: 0.5,
  py: 0.375,
  lineHeight: 1.15,
  boxShadow: "none",
  whiteSpace: "nowrap",
  justifyContent: "center",
  "& .MuiButton-startIcon": { mr: 0.25, ml: -0.1 },
};

const Permission = () => {
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const formatDateWithSuperadminTimezone = useSuperadminDateFormatter();
  const isPermissionMobileCard = useMediaQuery("(max-width: 775px)");

  // Define colors based on donation mode
  const colors = {
    bgClass: isDonationMode ? "bg-donor-color" : "bg-blue-color",
    borderClass: isDonationMode ? "border-donor-color" : "border-blue-color",
    textClass: isDonationMode ? "text-donor-color" : "text-blue-color",
    footerBgClass: isDonationMode ? "bg-green-color" : "bg-orange-color",
    borderColor: isDonationMode ? "#29B3E2" : "#0f2b46",
    undoButtonBg: isDonationMode ? "#66D59A" : "var(--amber)",
  };

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    handleAuth(navigate, location);
  }, []);
  const baseUrl = import.meta.env.VITE_BASE_API;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [companyData, setCompanyData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [countData, setCountData] = useState(0);
  const [sortField, setSortField] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  useEffect(() => {
    const handleDateFormat = () => {
      if (DateDecode?.Format) {
        setDateFormat(DateDecode?.Format);
      } else {
        setDateFormat("MM/DD/YYYY");
      }
    };
    handleDateFormat();
  }, [DateDecode]);
  const getData = async () => {
    setLoader(true);
    try {
      const res = await AxiosInstance.get(`/v1/permission`, {
        params: {
          pageSize: rowsPerPage,
          pageNumber: page,
          search: search || "",
          sortField: sortField,
          sortOrder: sortOrder,
        },
      });
      setCompanyData(res?.data?.data);
      setCountData(res?.data?.count);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData();
  }, [rowsPerPage, page, search, sortField, sortOrder]);

  const handleEditClick = (id) => {
    if (id) {
      navigate("/superadmin/add-permission", {
        state: {
          id,
          navigats: [...(location?.state?.navigats || []), "/add-permission"],
        },
      });
    }
  };

  const handleDelete = (id) => {
    const permissionToDelete = companyData.find(
      (permission) => permission?.PermissionId === id
    );

    if (!permissionToDelete) {
      showToast.error("Permission data not found");
      return;
    }

    setPermissionToDelete(permissionToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (reason) => {
    try {
      const response = await AxiosInstance.delete(`/v1/permission/${permissionToDelete?.PermissionId}`);
      if (response?.data?.statusCode === 200) {
        setTimeout(() => {
          showToast.success(response?.data?.message);
        }, 500);

        // Update local state immediately for better UX
        setCompanyData((prevData) =>
          prevData?.filter((permission) => permission?.PermissionId !== permissionToDelete?.PermissionId)
        );

        // Refresh table data from server after a short delay (same pattern as ManageTeamTable)
        setTimeout(async () => {
          await getData();
        }, 500);
      } else {
        setTimeout(() => {
          showToast.error(
            response?.data?.message || "Failed to delete permission"
          );
        }, 500);
      }
    } catch (error) {
      console.error("Error deleting permission:", error);
      setTimeout(() => {
        showToast.warning(
          error?.response?.data?.message || "Error deleting permission"
        );
      }, 500);
    }
    
    setIsDeleteModalOpen(false);
    setPermissionToDelete(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setPermissionToDelete(null);
  };

  const CollapseData = ({ data }) => {
    return (
      <Grid className="gap-4">
        <Row
          className="d-flex row mb-2 my-4 permission-box"
          style={{
            width: "100%",
            marginLeft: "0px",
            borderRadius: "14px",
            boxShadow:
              "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
            paddingLeft: "30px",
            paddingTop: "25px",
            paddingBottom: "20px",
          }}
        >
          <Row className="row">
            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={`${colors.textClass} mb-6 shcedulePermission`}>
                <Typography className="bold-text" fontSize={"15px"}>
                  Schedule
                </Typography>
              </Typography>
              {!(
                data?.Schedule?.ViewTheirOwnSchedule ||
                data?.Schedule?.ViewAndCompleteTheirOwnSchedule ||
                data?.Schedule?.EditTheirOwnSchedule ||
                data?.Schedule?.EditEveryonesSchedule ||
                data?.Schedule?.EditAndDeleteEveryonesSchedule ||
                data?.Schedule?.Delete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}

              {data?.Schedule?.ViewTheirOwnSchedule && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View their own schedule" />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.Schedule?.ViewAndCompleteTheirOwnSchedule && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View and complete their own schedule" />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.Schedule?.EditTheirOwnSchedule && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="Edit their own schedule" />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.Schedule?.EditEveryonesSchedule && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="Edit everyone's schedule" />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.Schedule?.EditAndDeleteEveryonesSchedule && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="Edit and delete everyone's schedule" />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.Schedule?.Delete && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="Delete" />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
            </Col>

            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Show Pricing
                </Typography>
              </Typography>
              {!data?.ShowPricing
                ?.AllowsEditingOfQuotesInvoicesAndLineItemsOnJobs && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.ShowPricing
                ?.AllowsEditingOfQuotesInvoicesAndLineItemsOnJobs && (
                <Typography
                  className={`${colors.textClass} mb-0 mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="Allows editing of quotes, invoices, and line items on jobs." />
                  <DoneIcon style={{ color: "#00EE64", fontSize: "18px" }} />
                </Typography>
              )}
            </Col>

            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography className="bold-text" fontSize={"15px"}>
                  Time Tracking and Timesheets
                </Typography>
              </Typography>
              {!(
                data?.TimeTrackingAndTimesheets?.ViewAndRecordTheirOwn ||
                data?.TimeTrackingAndTimesheets?.ViewRecordAndEditTheirOwn ||
                data?.TimeTrackingAndTimesheets?.ViewRecordAndEditEveryones
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.TimeTrackingAndTimesheets?.ViewAndRecordTheirOwn && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View and record their own timesheet" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
              {data?.TimeTrackingAndTimesheets?.ViewRecordAndEditTheirOwn && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View, record, and edit their own timesheet" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
              {data?.TimeTrackingAndTimesheets?.ViewRecordAndEditEveryones && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View, record, and edit everyone's timesheet" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
            </Col>
          </Row>

          <Row className="row">
            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography
                className={`${colors.textClass} giveMarginTop`}
                style={{ marginTop: "20px" }}
              >
                <Typography className="bold-text" fontSize={"15px"}>
                  Customers and Properties
                </Typography>
              </Typography>
              {!(
                data?.CustomersProperties?.ViewOnly ||
                data?.CustomersProperties?.ViewCreateAndEdit ||
                data?.CustomersProperties?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                      <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.CustomersProperties?.ViewOnly && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View only" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
              {data?.CustomersProperties?.ViewCreateAndEdit && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View, create, and edit" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
              {data?.CustomersProperties?.ViewCreateEditAndDelete && (
                <Typography
                  className={`${colors.textClass} mb-0 mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View, create, edit, and delete" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
            </Col>

            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography
                  className="bold-text giveMarginTop"
                  style={{ fontSize: "15px", marginTop: "20px" }}
                >
                  Notes
                </Typography>
              </Typography>
              {!( 
                data?.Notes?.ViewAllNotes ||
                data?.Notes?.ViewAndEditAll ||
                data?.Notes?.ViewEditAndDeleteAll ||
                data?.Notes?.ViewCreateEditAndDeleteAll
              ) && (
                <Typography
                  className="text-red-color mt-1 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              {data?.Notes?.ViewAllNotes && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View all notes" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
              {data?.Notes?.ViewAndEditAll && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View and edit all notes" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
              {(data?.Notes?.ViewEditAndDeleteAll || data?.Notes?.ViewCreateEditAndDeleteAll) && (
                <Typography
                  className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                  fontSize={"14px"}
                >
                  <TruncatedText text="View, edit, and delete all notes" />
                  <DoneIcon
                    className="mx-2"
                    style={{ color: "#00EE64", fontSize: "18px" }}
                  />
                </Typography>
              )}
            </Col>

            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography
                className={`${colors.textClass} giveMarginTop`}
                style={{ marginTop: "20px" }}
              >
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Expenses
                </Typography>
              </Typography>
              {!(
                data?.Expenses?.ViewRecordAndEditTheirOwn ||
                data?.Expenses?.ViewRecordAndEditEveryones
              ) && (
                <Typography
                  className="text-red-color mt-1 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Expenses?.ViewRecordAndEditTheirOwn && (
                  <>
                    <TruncatedText text="View, record, and edit their own" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Expenses?.ViewRecordAndEditEveryones && (
                  <>
                    <TruncatedText text="View, record, and edit everyone's" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>

 
          </Row>

          <Row className="row">
            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Customers
                </Typography>
              </Typography>
              {!(
                data?.Customers?.ViewOnly ||
                data?.Customers?.ViewCreateAndEdit ||
                data?.Customers?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className="text-blue-color mt-3 text-justify removeMarginTOp"
                fontSize={"14px"}
              >
                {data?.Customers?.ViewOnly && (
                  <>
                    <TruncatedText text="View only" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className="text-blue-color mt-3 text-justify removeMarginTOp"
                fontSize={"14px"}
              >
                {data?.Customers?.ViewCreateAndEdit && (
                  <>
                    <TruncatedText text="View, create, and edit" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className="text-blue-color mt-3 text-justify removeMarginTOp"
                fontSize={"14px"}
              >
                {data?.Customers?.ViewCreateEditAndDelete && (
                  <>
                    <TruncatedText text="View, create, edit, and delete" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>
            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Quotes
                </Typography>
              </Typography>
              {!(
                data?.Quotes?.ViewOnly ||
                data?.Quotes?.ViewCreateAndEdit ||
                data?.Quotes?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                    <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Quotes?.ViewOnly && (
                  <>
                    <TruncatedText text="View only" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Quotes?.ViewCreateAndEdit && (
                  <>
                    <TruncatedText text="View, create, and edit" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Quotes?.ViewCreateEditAndDelete && (
                  <>
                    <TruncatedText text="View, create, edit, and delete" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>
            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Materials & Labor
                </Typography>
              </Typography>
              {!(
                data?.MaterialsLabor?.ViewOnly ||
                data?.MaterialsLabor?.ViewCreateAndEdit ||
                data?.MaterialsLabor?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.MaterialsLabor?.ViewOnly && (
                  <>
                    <TruncatedText text="View only" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.MaterialsLabor?.ViewCreateAndEdit && (
                  <>
                    <TruncatedText text="View, create, and edit" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.MaterialsLabor?.ViewCreateEditAndDelete && (
                  <>
                    <TruncatedText text="View, create, edit, and delete" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>
            <Col
              className="col-xl-3 col-lg-6 col-md-6 col-sm-12 permission-row giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={colors.textClass}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                    Customers
                </Typography>
              </Typography>
              {!(
                data?.Customers?.ViewOnly ||
                data?.Customers?.ViewCreateAndEdit ||
                data?.Customers?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Customers?.ViewOnly && (
                  <>
                    <TruncatedText text="View only" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Customers?.ViewCreateAndEdit && (
                  <>
                    <TruncatedText text="View, create, and edit" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Customers?.ViewCreateEditAndDelete && (
                  <>
                    <TruncatedText text="View, create, edit, and delete" />
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>

            <Col
              className="col-xl-3 col-lg-4 col-md-6 col-sm-12 permission-row giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={`${colors.textClass} my-0`}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Contract
                </Typography>
              </Typography>
              {!(
                data?.Contract?.ViewOnly ||
                data?.Contract?.ViewCreateAndEdit ||
                data?.Contract?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                  <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Contract?.ViewOnly && (
                  <>
                    <TruncatedText text="View only" />{" "}
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Contract?.ViewCreateAndEdit && (
                  <>
                    <TruncatedText text="View, create, and edit" />{" "}
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Contract?.ViewCreateEditAndDelete && (
                  <>
                    <TruncatedText text="View, create, edit, and delete" />{" "}
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>

            <Col
              className="col-xl-3 col-lg-4 col-md-6 col-sm-12 giveMarginTop"
              xl={4}
              lg={4}
              md={12}
              sm={12}
              xs={12}
            >
              <Typography className={`${colors.textClass} my-0`}>
                <Typography className="bold-text" style={{ fontSize: "15px" }}>
                  Invoice
                </Typography>
              </Typography>
              {!(
                data?.Invoice?.ViewOnly ||
                data?.Invoice?.ViewCreateAndEdit ||
                data?.Invoice?.ViewCreateEditAndDelete
              ) && (
                <Typography
                  className="text-red-color mt-3 text-justify removeMarginTOp"
                  fontSize={"14px"}
                >
                    <TruncatedText text="No permissions assigned" />
                  <CloseIcon style={{ color: "#FF6347 ", fontSize: "18px" }} />
                </Typography>
              )}
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Invoice?.ViewOnly && (
                  <>
                    <TruncatedText text="View only" />{" "}
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Invoice?.ViewCreateAndEdit && (
                  <>
                    <TruncatedText text="View, create, and edit" />{" "}
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
              <Typography
                className={`${colors.textClass} mt-3 text-justify removeMarginTOp`}
                fontSize={"14px"}
              >
                {data?.Invoice?.ViewCreateEditAndDelete && (
                  <>
                    <TruncatedText text="View, create, edit, and delete" />{" "}
                    <DoneIcon
                      className="mx-2"
                      style={{ color: "#00EE64", fontSize: "18px" }}
                    />
                  </>
                )}
              </Typography>
            </Col>
          </Row>
        </Row>
      </Grid>
    );
  };

  const cellData = companyData?.map((item, index) => {
    return {
      key: item?.PermissionId,
      value: [
        <>
          <Typography className="bold-text">
            <TruncatedText text={item?.Title || "Title not available"} />
          </Typography>{" "}
          <TruncatedText
            text={item?.Description || "Description not available"}
          />
        </>,
        formatDateWithSuperadminTimezone(item?.createdAt, {
          dateFormat: "MM/DD/YYYY",
          includeSeconds: false
        }),
        isPermissionMobileCard ? (
          <Box
            key={`permission-actions-${item?.PermissionId}`}
            className="permission-mobile-actions customer-list-actions-mobile"
            sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
          >
            <MuiButton
              type="button"
              size="small"
              variant="contained"
              disableElevation
              fullWidth
              startIcon={
                <EditIcon
                  color={PERMISSION_EDIT_ICON_COLOR}
                  style={{ marginRight: 0, fontSize: "16px" }}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(item?.PermissionId);
              }}
              sx={{
                ...permissionMobileActionBtnBase,
                backgroundColor: PERMISSION_EDIT_ACTION_BG,
                color: PERMISSION_EDIT_ICON_COLOR,
                border: "1px solid rgba(4, 120, 87, 0.22)",
                "&:hover": {
                  backgroundColor: "#bbf7d0",
                  borderColor: "rgba(4, 120, 87, 0.35)",
                },
              }}
            >
              Edit
            </MuiButton>
            <MuiButton
              type="button"
              size="small"
              variant="contained"
              disableElevation
              fullWidth
              startIcon={
                <DeleteIcone
                  className="customerEditImgToEdit"
                  color={PERMISSION_DELETE_ICON_COLOR}
                  style={{ marginRight: 0, fontSize: "16px" }}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(item?.PermissionId);
              }}
              sx={{
                ...permissionMobileActionBtnBase,
                backgroundColor: PERMISSION_DELETE_ACTION_BG,
                color: PERMISSION_DELETE_ICON_COLOR,
                border: "1px solid rgba(185, 28, 28, 0.2)",
                "&:hover": {
                  backgroundColor: "#fecaca",
                  borderColor: "rgba(185, 28, 28, 0.35)",
                },
              }}
            >
              Delete
            </MuiButton>
          </Box>
        ) : (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8 }}
            key={`permission-actions-${item?.PermissionId}`}
          >
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(item?.PermissionId);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: PERMISSION_EDIT_ACTION_BG,
                verticalAlign: "middle",
                lineHeight: 0,
                border: "1px solid rgba(4, 120, 87, 0.18)",
                boxShadow: "var(--shadow-sm)",
                cursor: "pointer",
              }}
            >
              <EditIcon
                color={PERMISSION_EDIT_ICON_COLOR}
                style={{ marginRight: 0 }}
              />
            </Box>
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(item?.PermissionId);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: PERMISSION_DELETE_ACTION_BG,
                verticalAlign: "middle",
                lineHeight: 0,
                border: "1px solid rgba(185, 28, 28, 0.15)",
                boxShadow: "var(--shadow-sm)",
                cursor: "pointer",
              }}
            >
              <DeleteIcone
                className="customerEditImgToEdit"
                color={PERMISSION_DELETE_ICON_COLOR}
                style={{ marginRight: 0 }}
              />
            </Box>
          </div>
        ),
      ],
      component: item,
    };
  });

  return (
    <>
      <Grid className="justify-content-center align-items-center mb-3 industry">
        <JobberListCard
          title="Permission Steps"
          totalCount={countData}
          toolbar={
            <Grid className="search_box companyPlanSearch">
              <JobberSearch search={search} setSearch={setSearch} />
            </Grid>
          }
          addButton={{
            label: "Add Permission",
            onClick: () => {
              navigate(`/superadmin/add-permission`, {
                state: {
                  navigats: [
                    ...(location?.state?.navigats || []),
                    "/add-permission",
                  ],
                },
              });
            },
            isDonationMode,
          }}
          loader={loader}
          footer={
            <JobberPagination
              totalData={countData}
              currentData={rowsPerPage}
              dataPerPage={rowsPerPage}
              pageItems={[10, 25, 50]}
              page={page}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
            />
          }
        >
          <Grid>
            <JobberTable
              className="permission-head-title permission-jobber-table"
              mobileCardPrimaryColumnIndex={0}
              mobileCardSubtitleColumnIndex={1}
              headerData={[
                { label: "Title And Description", field: "Title" },
                { label: "Created On", field: "createdAt" },
                { label: "Actions" },
              ]}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
              sortField={sortField}
              cellData={cellData}
              CollapseComponent={(data) => CollapseData(data)}
              isCollapse={true}
              page={page}
              isNavigate={false}
            />
          </Grid>
        </JobberListCard>
      </Grid>
      <CustomSwal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Are you sure?"
        text="Once deleted, you will not be able to recover!"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        placeholder="Enter reason for deletion"
        requireInput={true}
        isDonationMode={isDonationMode}
      />
    </>
  );
};

export default Permission;
